import React, { useState, useEffect } from 'react';
import { PostInteractions, QuotedPostEmbed } from './XPlatformFeatures';

// User Profile Component
const UserProfile = ({ 
  userId, 
  token, 
  onBack, 
  onLike, 
  onRepost, 
  onQuote, 
  onComment, 
  onShare,
  currentUserId 
}) => {
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [postsInteractions, setPostsInteractions] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('posts'); // posts, followers, following
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [bioText, setBioText] = useState('');
  const [savingBio, setSavingBio] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await fetch(`${API_URL}/api/users/${userId}/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setProfile(data);
          setBioText(data.bio || '');
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };

    const fetchPosts = async () => {
      try {
        const response = await fetch(`${API_URL}/api/users/${userId}/posts?limit=50`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setPosts(data);
          
          // Fetch interactions for posts
          if (data.length > 0) {
            const postIds = data.map(p => p.id);
            const interactionsResponse = await fetch(`${API_URL}/api/world-chat/posts/interactions`, {
              method: 'POST',
              headers: { 
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(postIds)
            });
            if (interactionsResponse.ok) {
              const interactionsData = await interactionsResponse.json();
              setPostsInteractions(interactionsData);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching posts:', error);
      }
    };

    if (userId && token) {
      setLoading(true);
      Promise.all([fetchProfile(), fetchPosts()]).finally(() => setLoading(false));
    }
  }, [userId, token, API_URL]);

  // Fetch followers
  const fetchFollowers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/${userId}/followers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFollowers(data);
      }
    } catch (error) {
      console.error('Error fetching followers:', error);
    }
  };

  // Fetch following
  const fetchFollowing = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/${userId}/following`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFollowing(data);
      }
    } catch (error) {
      console.error('Error fetching following:', error);
    }
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'followers' && followers.length === 0) {
      fetchFollowers();
    } else if (tab === 'following' && following.length === 0) {
      fetchFollowing();
    }
  };

  // Handle follow/unfollow
  const handleFollowToggle = async () => {
    if (!profile) return;
    
    try {
      if (profile.is_following) {
        await fetch(`${API_URL}/api/users/unfollow/${userId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setProfile(prev => ({ 
          ...prev, 
          is_following: false,
          followers_count: prev.followers_count - 1 
        }));
      } else {
        await fetch(`${API_URL}/api/users/follow/${userId}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setProfile(prev => ({ 
          ...prev, 
          is_following: true,
          followers_count: prev.followers_count + 1 
        }));
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
    }
  };

  // Handle bio save
  const handleSaveBio = async () => {
    setSavingBio(true);
    try {
      const response = await fetch(`${API_URL}/api/users/profile/bio`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ bio: bioText })
      });
      if (response.ok) {
        setProfile(prev => ({ ...prev, bio: bioText }));
        setIsEditingBio(false);
      }
    } catch (error) {
      console.error('Error saving bio:', error);
    } finally {
      setSavingBio(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ro-RO', { month: 'long', year: 'numeric' });
  };

  // Format post date
  const formatPostDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'acum';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return date.toLocaleDateString('ro-RO', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="p-4 text-center text-gray-400">
        Utilizatorul nu a fost găsit
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-gray-900/80 backdrop-blur-md border-b border-gray-800">
        <div className="flex items-center px-4 py-3">
          <button 
            onClick={onBack}
            className="p-2 hover:bg-gray-800 rounded-full transition-colors mr-4"
            data-testid="profile-back-button"
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold text-white">
              {profile.first_name} {profile.last_name}
            </h1>
            <p className="text-sm text-gray-500">{profile.posts_count} postări</p>
          </div>
        </div>
      </div>

      {/* Profile Header */}
      <div className="relative">
        {/* Cover Image Placeholder */}
        <div className="h-32 sm:h-48 bg-gradient-to-r from-cyan-600 to-purple-600"></div>
        
        {/* Avatar */}
        <div className="absolute left-4 -bottom-16">
          <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full border-4 border-gray-900 overflow-hidden bg-gradient-to-br from-purple-500 to-pink-500">
            {profile.avatar_url ? (
              <img 
                src={profile.avatar_url} 
                alt={profile.nickname}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white text-3xl sm:text-4xl font-bold">
                {(profile.first_name?.[0] || profile.nickname?.[0] || 'U').toUpperCase()}
              </div>
            )}
          </div>
        </div>

        {/* Follow/Edit Button */}
        <div className="absolute right-4 bottom-4">
          {profile.is_own_profile ? (
            <button
              onClick={() => setIsEditingBio(true)}
              className="px-4 py-2 border border-gray-600 text-white rounded-full font-semibold hover:bg-gray-800 transition-colors"
              data-testid="edit-profile-button"
            >
              Editează profilul
            </button>
          ) : (
            <button
              onClick={handleFollowToggle}
              className={`px-4 py-2 rounded-full font-semibold transition-colors ${
                profile.is_following 
                  ? 'bg-transparent border border-gray-600 text-white hover:border-red-500 hover:text-red-500' 
                  : 'bg-white text-black hover:bg-gray-200'
              }`}
              data-testid="follow-button"
            >
              {profile.is_following ? 'Following' : 'Follow'}
            </button>
          )}
        </div>
      </div>

      {/* Profile Info */}
      <div className="mt-20 px-4">
        <h2 className="text-xl font-bold text-white">
          {profile.first_name} {profile.last_name}
        </h2>
        <p className="text-gray-500">@{profile.nickname}</p>
        
        {/* Bio */}
        {isEditingBio ? (
          <div className="mt-3">
            <textarea
              value={bioText}
              onChange={(e) => setBioText(e.target.value)}
              maxLength={160}
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white resize-none focus:outline-none focus:border-cyan-500"
              rows={3}
              placeholder="Spune ceva despre tine..."
              autoFocus
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-sm text-gray-500">{bioText.length}/160</span>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setIsEditingBio(false);
                    setBioText(profile.bio || '');
                  }}
                  className="px-4 py-1.5 text-gray-400 hover:text-white transition-colors"
                >
                  Anulează
                </button>
                <button
                  onClick={handleSaveBio}
                  disabled={savingBio}
                  className="px-4 py-1.5 bg-cyan-500 text-white rounded-full font-semibold hover:bg-cyan-600 disabled:opacity-50 transition-colors"
                >
                  {savingBio ? 'Se salvează...' : 'Salvează'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          profile.bio && <p className="mt-3 text-white">{profile.bio}</p>
        )}

        {/* Join Date */}
        <div className="flex items-center mt-3 text-gray-500 text-sm">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          S-a înscris în {formatDate(profile.created_at)}
        </div>

        {/* Stats */}
        <div className="flex space-x-5 mt-3">
          <button 
            onClick={() => handleTabChange('following')}
            className="hover:underline"
            data-testid="following-count"
          >
            <span className="font-bold text-white">{profile.following_count}</span>
            <span className="text-gray-500 ml-1">Following</span>
          </button>
          <button 
            onClick={() => handleTabChange('followers')}
            className="hover:underline"
            data-testid="followers-count"
          >
            <span className="font-bold text-white">{profile.followers_count}</span>
            <span className="text-gray-500 ml-1">Followers</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-800 mt-4">
        <button
          onClick={() => handleTabChange('posts')}
          className={`flex-1 py-4 text-center font-semibold transition-colors ${
            activeTab === 'posts' 
              ? 'text-white border-b-2 border-cyan-500' 
              : 'text-gray-500 hover:bg-gray-800/50'
          }`}
          data-testid="posts-tab"
        >
          Postări
        </button>
        <button
          onClick={() => handleTabChange('followers')}
          className={`flex-1 py-4 text-center font-semibold transition-colors ${
            activeTab === 'followers' 
              ? 'text-white border-b-2 border-cyan-500' 
              : 'text-gray-500 hover:bg-gray-800/50'
          }`}
          data-testid="followers-tab"
        >
          Followers
        </button>
        <button
          onClick={() => handleTabChange('following')}
          className={`flex-1 py-4 text-center font-semibold transition-colors ${
            activeTab === 'following' 
              ? 'text-white border-b-2 border-cyan-500' 
              : 'text-gray-500 hover:bg-gray-800/50'
          }`}
          data-testid="following-tab"
        >
          Following
        </button>
      </div>

      {/* Content */}
      <div className="pb-20">
        {activeTab === 'posts' && (
          <div>
            {posts.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                {profile.is_own_profile 
                  ? 'Nu ai postări încă. Creează prima ta postare!' 
                  : 'Acest utilizator nu are postări încă.'}
              </div>
            ) : (
              posts.map(post => (
                <div 
                  key={post.id} 
                  className="border-b border-gray-800 p-4 hover:bg-gray-800/30 transition-colors"
                  data-testid={`profile-post-${post.id}`}
                >
                  {/* Post Header */}
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex-shrink-0 overflow-hidden">
                      {post.user_avatar ? (
                        <img src={post.user_avatar} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white font-bold">
                          {(post.user_name?.[0] || 'U').toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className="font-bold text-white truncate">{post.user_name}</span>
                        <span className="text-gray-500 truncate">@{post.user_nickname}</span>
                        <span className="text-gray-600">·</span>
                        <span className="text-gray-500 text-sm">{formatPostDate(post.created_at)}</span>
                      </div>
                      
                      {/* Post Content */}
                      <p className="text-white mt-1 whitespace-pre-wrap">{post.content}</p>
                      
                      {/* Images */}
                      {post.images && post.images.length > 0 && (
                        <div className="mt-3 rounded-2xl overflow-hidden">
                          <img 
                            src={`${API_URL}${post.images[0].url}`}
                            alt="Post image"
                            className="w-full max-h-96 object-cover"
                          />
                        </div>
                      )}

                      {/* Quoted Post */}
                      {post.quoted_post && (
                        <div className="mt-3">
                          <QuotedPostEmbed post={post.quoted_post} />
                        </div>
                      )}

                      {/* Interactions */}
                      <div className="mt-3">
                        <PostInteractions
                          postId={post.id}
                          interactions={postsInteractions[post.id] || {}}
                          onLike={onLike}
                          onRepost={onRepost}
                          onQuote={onQuote}
                          onComment={onComment}
                          onShare={onShare}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'followers' && (
          <div>
            {followers.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Niciun follower încă
              </div>
            ) : (
              followers.map(follower => (
                <UserListItem 
                  key={follower.id}
                  user={follower}
                  currentUserId={currentUserId}
                  token={token}
                />
              ))
            )}
          </div>
        )}

        {activeTab === 'following' && (
          <div>
            {following.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Nu urmărește pe nimeni încă
              </div>
            ) : (
              following.map(user => (
                <UserListItem 
                  key={user.id}
                  user={user}
                  currentUserId={currentUserId}
                  token={token}
                />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// User List Item Component (for followers/following lists)
const UserListItem = ({ user, currentUserId, token }) => {
  const [isFollowing, setIsFollowing] = useState(user.is_following);
  const [loading, setLoading] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleFollowToggle = async () => {
    if (user.id === currentUserId) return;
    setLoading(true);
    
    try {
      if (isFollowing) {
        await fetch(`${API_URL}/api/users/unfollow/${user.id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setIsFollowing(false);
      } else {
        await fetch(`${API_URL}/api/users/follow/${user.id}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setIsFollowing(true);
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-between p-4 hover:bg-gray-800/30 transition-colors border-b border-gray-800">
      <div className="flex items-center space-x-3">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex-shrink-0 overflow-hidden">
          {user.avatar_url ? (
            <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white font-bold">
              {(user.first_name?.[0] || user.nickname?.[0] || 'U').toUpperCase()}
            </div>
          )}
        </div>
        <div>
          <p className="font-bold text-white">
            {user.first_name} {user.last_name}
          </p>
          <p className="text-gray-500">@{user.nickname}</p>
          {user.bio && (
            <p className="text-gray-400 text-sm mt-1 line-clamp-1">{user.bio}</p>
          )}
        </div>
      </div>
      
      {user.id !== currentUserId && (
        <button
          onClick={handleFollowToggle}
          disabled={loading}
          className={`px-4 py-1.5 rounded-full font-semibold text-sm transition-colors ${
            isFollowing 
              ? 'bg-transparent border border-gray-600 text-white hover:border-red-500 hover:text-red-500' 
              : 'bg-white text-black hover:bg-gray-200'
          } ${loading ? 'opacity-50' : ''}`}
          data-testid={`follow-user-${user.id}`}
        >
          {loading ? '...' : (isFollowing ? 'Following' : 'Follow')}
        </button>
      )}
    </div>
  );
};

export default UserProfile;
